<template>
  <div class="revision-root">

    <!-- ── Empty state ── -->
    <div v-if="items.length === 0" class="revision-empty">
      <v-icon size="48" color="grey lighten-2">mdi-table-off</v-icon>
      <p class="revision-empty-text">No se encontraron productos para comparar.</p>
    </div>

    <template v-else>

      <!-- ══════ GLOBAL CONTROLS ══════ -->
      <div class="gc-bar">
        <div class="gc-bar-left">
          <v-icon x-small color="grey darken-1" class="mr-1">mdi-tune</v-icon>
          <span class="gc-label">Controles globales</span>
        </div>
        <div class="gc-bar-right">
          <div class="gc-descuento">
            <input
              v-model.number="descuentoGlobal"
              type="number"
              min="0"
              max="100"
              placeholder="Descuento global (%)"
              class="gc-input"
              @input="aplicarDescuentoGlobal"
            />
            <span class="gc-input-suffix">%</span>
          </div>
          <button class="gc-btn gc-btn--primary" @click="marcarTodosAsociar">
            <v-icon x-small class="mr-1">mdi-check-all</v-icon>
            Actualizar todos
          </button>
          <button class="gc-btn" @click="marcarTodosIgnorar">
            <v-icon x-small class="mr-1">mdi-close</v-icon>
            Omitir todos
          </button>
        </div>
      </div>

      <!-- ══════ TABS ══════ -->
      <div class="tab-row">
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': tabActivo === 0 }"
          @click="tabActivo = 0"
        >
          Todos
          <span class="tab-count">{{ items.length }}</span>
        </button>
        <button
          class="tab-pill"
          :class="{ 'tab-pill--active': tabActivo === 1 }"
          @click="tabActivo = 1"
        >
          Con match
          <span class="tab-count">{{ itemsConMatch.length }}</span>
        </button>
        <button
          class="tab-pill"
          :class="{
            'tab-pill--active': tabActivo === 2,
            'tab-pill--warn': itemsSinMatch.length > 0,
          }"
          @click="tabActivo = 2"
        >
          Sin match
          <span class="tab-count">{{ itemsSinMatch.length }}</span>
        </button>
      </div>

      <!-- ══════ LEGEND ══════ -->
      <div class="legend-row">
        <span class="legend-item"><span class="legend-dot legend-dot--asociar"></span>Actualizar</span>
        <span class="legend-item"><span class="legend-dot legend-dot--ignorar"></span>Omitir</span>
        <span class="legend-item"><span class="legend-dot legend-dot--pending"></span>Sin match aún</span>
      </div>

      <!-- ══════ TABLE ══════ -->
      <div class="rev-table-wrapper">
        <div class="rev-table-scroll">
          <table class="rev-table" cellspacing="0" cellpadding="0">
            <thead>
              <tr>
                <th class="rth rth-fixed rth-star"></th>
                <th class="rth rth-fixed rth-calc"></th>
                <th class="rth">En el archivo</th>
                <th class="rth rth-match">Match</th>
                <th class="rth rth-producto">Producto en depósito</th>
                <th class="rth">P. compra actual</th>
                <th class="rth">P. venta actual</th>
                <th class="rth rth-input">P. compra nuevo</th>
                <th class="rth rth-input-sm">Desc. %</th>
                <th class="rth rth-input">P. venta nuevo</th>
                <th class="rth rth-dif">Dif. %</th>
                <th class="rth rth-accion">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in itemsEnTab"
                :key="item._tabIndex"
                class="rev-row"
                :class="filaClass(item, item._tabIndex)"
              >
                <!-- ★ Recomendado -->
                <td class="rtd rtd-star">
                  <v-icon v-if="item.recomendado" small color="amber darken-2">mdi-star</v-icon>
                </td>

                <!-- Calculator -->
                <td class="rtd rtd-calc">
                  <button class="calc-btn" @click="abrirModalCalculoFila(item._tabIndex)" title="Ver desglose de precio">
                    <v-icon small color="grey darken-1">mdi-calculator-variant-outline</v-icon>
                  </button>
                </td>

                <!-- En el archivo -->
                <td class="rtd rtd-archivo">
                  <div class="archivo-nombre">{{ item.nombreArchivo || "—" }}</div>
                  <div v-if="item.codigoBarrasProveedor" class="archivo-codigo">{{ item.codigoBarrasProveedor }}</div>
                </td>

                <!-- Match badge -->
                <td class="rtd rtd-match">
                  <span
                    class="match-tag"
                    :style="getMatchTagStyle(item.metodoMatch)"
                  >{{ getMatchLabel(item.metodoMatch) }}</span>
                </td>

                <!-- Producto en depósito -->
                <td class="rtd rtd-producto">
                  <v-autocomplete
                    v-if="depositoId"
                    :value="getProductoParaFila(item, item._tabIndex)"
                    :items="getItemsAutocomplete(item._tabIndex, item)"
                    :loading="loadingBusqueda[item._tabIndex]"
                    item-text="nombre"
                    item-value="id"
                    hide-no-data dense hide-details solo flat clearable return-object
                    placeholder="Buscar producto..."
                    class="table-input-producto rev-producto-select"
                    @input="onSeleccionManual(item._tabIndex, $event)"
                    @update:search-input="onSearchProducto(item._tabIndex, $event)"
                  >
                    <template v-slot:selection="{ item: sel }">{{ getProductoNombre(sel) }}</template>
                    <template v-slot:item="{ item: row }">
                      {{ getProductoNombre(row) }}
                      <span v-if="row.codigoBarras" class="caption grey--text ml-1">({{ row.codigoBarras }})</span>
                    </template>
                  </v-autocomplete>
                  <span v-else class="caption grey--text">Sin depósito</span>
                </td>

                <!-- Precio compra actual -->
                <td class="rtd rtd-precio-actual">
                  <template v-if="item.precioCompraActual != null">
                    <template v-if="item.descuentoActual && item.descuentoActual > 0">
                      <span class="precio-tachado">${{ formatNum(item.precioCompraActual) }}</span>
                      <span class="precio-neto ml-1">${{ formatNum(item.precioActualNeto != null ? item.precioActualNeto : item.precioCompraActual) }}</span>
                      <span class="descuento-badge ml-1">-{{ formatNum(item.descuentoActual) }}%</span>
                    </template>
                    <template v-else>
                      <span class="precio-neto">${{ formatNum(item.precioCompraActual) }}</span>
                    </template>
                  </template>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- Precio venta actual -->
                <td class="rtd">
                  <span v-if="getPrecioVentaActualMostrado(item, item._tabIndex) != null" class="precio-neto">
                    ${{ formatNum(getPrecioVentaActualMostrado(item, item._tabIndex)) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- Precio compra nuevo (editable) -->
                <td class="rtd rtd-editable">
                  <v-text-field
                    :value="getPrecioCompraEfectivo(item, item._tabIndex)"
                    type="number" :min="0" step="0.01"
                    dense solo flat hide-details
                    class="table-input" prefix="$"
                    @input="onPrecioCompraInput(item._tabIndex, $event)"
                  />
                </td>

                <!-- Descuento nuevo % (editable) -->
                <td class="rtd rtd-editable">
                  <v-text-field
                    :value="getDescuentoEfectivo(item, item._tabIndex)"
                    type="number" :min="0" :max="100"
                    dense solo flat hide-details
                    class="table-input-small" suffix="%"
                    @input="onDescuentoInput(item._tabIndex, $event)"
                  />
                </td>

                <!-- Precio venta nuevo (editable / placeholder calc) -->
                <td class="rtd rtd-editable">
                  <v-text-field
                    :value="getPrecioVentaManualOverride(item._tabIndex)"
                    type="number" :min="0" step="0.01"
                    dense solo flat hide-details
                    class="table-input table-input-precio-nuevo" prefix="$"
                    :placeholder="getPrecioVentaNuevoCalculado(item, item._tabIndex) != null ? formatNum(getPrecioVentaNuevoCalculado(item, item._tabIndex)) : '—'"
                    @input="onPrecioVentaManualInput(item._tabIndex, $event)"
                  />
                </td>

                <!-- Diferencia % -->
                <td class="rtd rtd-dif">
                  <span
                    v-if="getDiferenciaEfectiva(item, item._tabIndex) != null"
                    class="diff-badge"
                    :class="diffBadgeClass(item, item._tabIndex)"
                  >
                    {{ formatDiferencia(getDiferenciaEfectiva(item, item._tabIndex)) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- Acción -->
                <td class="rtd rtd-accion">
                  <template v-if="getProductoParaFila(item, item._tabIndex)">
                    <div class="row-actions">
                      <button
                        class="action-btn"
                        :class="acciones[item._tabIndex] === 'ignorar' ? 'action-btn--ignorar-on' : 'action-btn--ignorar'"
                        @click="setAccion(item._tabIndex, 'ignorar')"
                      >Omitir</button>
                      <button
                        class="action-btn"
                        :class="acciones[item._tabIndex] === 'asociar' ? 'action-btn--asociar-on' : 'action-btn--asociar'"
                        @click="setAccion(item._tabIndex, 'asociar')"
                      >Actualizar</button>
                    </div>
                  </template>
                  <span v-else class="text-muted caption">Sin match</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="rev-fade-right"></div>
      </div>

      <!-- ── Footer summary ── -->
      <div v-if="cantidadAsociar > 0" class="revision-summary">
        <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
        <strong>{{ cantidadAsociar }}</strong> producto{{ cantidadAsociar !== 1 ? 's' : '' }} marcado{{ cantidadAsociar !== 1 ? 's' : '' }} para actualizar
      </div>

    </template>

    <!-- ══════ MODAL CÁLCULO (idéntico al original) ══════ -->
    <v-dialog v-model="modalCalculoPrecios" max-width="720">
      <v-card>
        <v-card-title class="pb-2 d-flex align-center" style="word-break:break-word;line-height:1.35">
          <v-icon color="primary" class="flex-shrink-0 mr-3">mdi-calculator-variant-outline</v-icon>
          <div style="flex:1 1 auto;min-width:0">
            <span>{{ $t("actualizarPreciosProveedor.modalCalculoTitulo") }}</span>
            <template v-if="etiquetaFilaCalculoModal">
              <span class="grey--text text--darken-1 mx-1">—</span>
              <span>{{ etiquetaFilaCalculoModal }}</span>
            </template>
          </div>
        </v-card-title>
        <v-card-text class="pt-2 pb-0">
          <v-alert v-if="modalCalculoData && modalCalculoData.sinProducto" type="info" dense text border="left" colored-border class="mb-0">
            {{ $t("actualizarPreciosProveedor.modalCalculoSinProducto") }}
          </v-alert>
          <v-row v-else-if="modalCalculoData" dense>
            <v-col cols="12" class="pb-3">
              <v-sheet class="pa-4 rounded-lg" outlined style="background:#fafafa">
                <div class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 mb-3 d-flex align-center">
                  <v-icon small left color="blue-grey">mdi-tune-variant</v-icon>
                  {{ $t("actualizarPreciosProveedor.modalConfigProductoTitulo") }}
                </div>
                <div style="display:flex;flex-wrap:wrap;gap:16px 24px">
                  <div>
                    <div class="caption grey--text text--darken-1 mb-1">{{ $t("actualizarPreciosProveedor.modalConfigIva") }}</div>
                    <div class="body-1 font-weight-bold">{{ formatNum(modalCalculoData.ivaProducto) }}%</div>
                  </div>
                  <div>
                    <div class="caption grey--text text--darken-1 mb-1">{{ $t("actualizarPreciosProveedor.modalConfigMargen") }}</div>
                    <div class="body-1 font-weight-bold">{{ formatNum(modalCalculoData.margenProducto) }}%</div>
                  </div>
                  <div style="flex:1 1 200px;min-width:180px">
                    <div class="caption grey--text text--darken-1 mb-1">{{ $t("actualizarPreciosProveedor.modalConfigRedondeo") }}</div>
                    <div class="body-2 font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.tipoRedondeoProducto) }}</div>
                  </div>
                </div>
                <p class="caption grey--text mb-0 mt-3" style="border-top:1px solid rgba(0,0,0,.06);padding-top:12px">{{ $t("actualizarPreciosProveedor.modalConfigAyuda") }}</p>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="6">
              <v-sheet class="pa-4 rounded-lg" elevation="1" style="background:#f7f9ff;border:1px solid rgba(25,118,210,.14)">
                <div class="mb-3 d-flex align-center">
                  <v-icon small left color="blue darken-1">mdi-database-outline</v-icon>
                  <span class="subtitle-1 font-weight-bold blue-grey--text text--darken-2">{{ $t("actualizarPreciosProveedor.modalBloqueActual") }}</span>
                </div>
                <v-alert v-if="!modalCalculoData.actual.ok" type="warning" dense text class="mb-0 caption">{{ $t("actualizarPreciosProveedor.modalCalculoSinPrecioCompra") }}</v-alert>
                <div v-else class="desglose-pasos">
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoPrecioCompra") }}</div><div class="dp-val">{{ moneda(modalCalculoData.actual.precioCompra) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoDescuentoProveedor") }} <span class="grey--text">({{ formatNum(modalCalculoData.actual.descuentoPct) }}%)</span></div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.actual.precioCompra) }} × (1 − {{ formatNum(modalCalculoData.actual.descuentoPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoCostoNeto") }}</div><div class="dp-val">{{ moneda(modalCalculoData.actual.neto) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarIva") }}</div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.actual.neto) }} × (1 + {{ formatNum(modalCalculoData.actual.ivaPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoTrasIva") }}</div><div class="dp-val">{{ moneda(modalCalculoData.actual.trasIva) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarMargen") }}</div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.actual.trasIva) }} × (1 + {{ formatNum(modalCalculoData.actual.margenPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAntesRedondeo") }}</div><div class="dp-val">{{ moneda(modalCalculoData.actual.antesRedondeo) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarRedondeo") }}</div><div class="dp-detail caption"><span class="font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.actual.tipoRedondeo) }}</span><span v-if="!modalCalculoData.actual.redondeoAplica" class="grey--text"> — {{ $t("actualizarPreciosProveedor.modalSinRedondeo") }}</span></div><div v-if="modalCalculoData.actual.redondeoAplica" class="dp-formula caption grey--text mt-1">{{ moneda(modalCalculoData.actual.antesRedondeo) }} → {{ moneda(modalCalculoData.actual.precioFinal) }}</div></div>
                  <div class="dp-final mt-3 pa-3 rounded-lg"><div class="body-2 mb-1">{{ $t("actualizarPreciosProveedor.modalPasoPrecioVentaFinal") }}</div><div class="dp-final-val primary--text">{{ moneda(modalCalculoData.actual.precioFinal) }}</div></div>
                  <v-alert v-if="modalCalculoData.notaPrecioGuardadoActual" dense text type="info" class="mt-3 mb-0 caption">{{ $t("actualizarPreciosProveedor.modalNotaPrecioGuardado", { monto: moneda(modalCalculoData.precioVentaGuardado) }) }}</v-alert>
                </div>
              </v-sheet>
            </v-col>
            <v-col cols="12" md="6">
              <v-sheet class="pa-4 rounded-lg" elevation="1" :style="modalCalculoData.nuevoMayorQueActual ? 'background:#fff8f7;border:1px solid rgba(198,40,40,.22)' : 'background:#f4fbf9;border:1px solid rgba(0,121,107,.14)'">
                <div class="mb-3 d-flex align-center">
                  <v-icon small left :color="modalCalculoData.nuevoMayorQueActual ? 'red darken-2' : 'teal darken-1'">mdi-file-excel-outline</v-icon>
                  <span class="subtitle-1 font-weight-bold blue-grey--text text--darken-2">{{ $t("actualizarPreciosProveedor.modalBloqueNuevo") }}</span>
                </div>
                <v-alert v-if="!modalCalculoData.nuevo.ok" type="warning" dense text class="mb-0 caption">{{ $t("actualizarPreciosProveedor.modalCalculoSinPrecioCompra") }}</v-alert>
                <div v-else class="desglose-pasos">
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoPrecioCompra") }}</div><div class="dp-val">{{ moneda(modalCalculoData.nuevo.precioCompra) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoDescuentoProveedor") }} <span class="grey--text">({{ formatNum(modalCalculoData.nuevo.descuentoPct) }}%)</span></div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.nuevo.precioCompra) }} × (1 − {{ formatNum(modalCalculoData.nuevo.descuentoPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoCostoNeto") }}</div><div class="dp-val">{{ moneda(modalCalculoData.nuevo.neto) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarIva") }}</div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.nuevo.neto) }} × (1 + {{ formatNum(modalCalculoData.nuevo.ivaPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoTrasIva") }}</div><div class="dp-val">{{ moneda(modalCalculoData.nuevo.trasIva) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarMargen") }}</div><div class="dp-formula caption grey--text">{{ moneda(modalCalculoData.nuevo.trasIva) }} × (1 + {{ formatNum(modalCalculoData.nuevo.margenPct) }} ÷ 100)</div></div>
                  <div class="dp dp--hl"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAntesRedondeo") }}</div><div class="dp-val">{{ moneda(modalCalculoData.nuevo.antesRedondeo) }}</div></div>
                  <v-divider class="my-2" />
                  <div class="dp"><div class="dp-label">{{ $t("actualizarPreciosProveedor.modalPasoAplicarRedondeo") }}</div><div class="dp-detail caption"><span class="font-weight-medium">{{ etiquetaRedondeo(modalCalculoData.nuevo.tipoRedondeo) }}</span><span v-if="!modalCalculoData.nuevo.redondeoAplica" class="grey--text"> — {{ $t("actualizarPreciosProveedor.modalSinRedondeo") }}</span></div><div v-if="modalCalculoData.nuevo.redondeoAplica" class="dp-formula caption grey--text mt-1">{{ moneda(modalCalculoData.nuevo.antesRedondeo) }} → {{ moneda(modalCalculoData.nuevo.precioFinal) }}</div></div>
                  <div class="dp-final mt-3 pa-3 rounded-lg"><div class="body-2 mb-1">{{ $t("actualizarPreciosProveedor.modalPasoPrecioVentaFinal") }}</div><div class="dp-final-val" :class="modalCalculoData.nuevoMayorQueActual ? 'error--text' : 'teal--text text--darken-2'">{{ moneda(modalCalculoData.nuevo.precioFinal) }}</div></div>
                </div>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pt-2 pb-4 px-4">
          <v-spacer />
          <v-btn text color="primary" @click="cerrarModalCalculoFila">{{ $t("actualizarPreciosProveedor.cerrar") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ProductoProvider from "../../../producto/providers/ProductoProvider";

export default {
  name: "ImportarListaPreciosPasoRevision",
  props: {
    items:      { type: Array,  default: () => [] },
    depositoId: { type: String, default: null },
    modo:       { type: String, default: "SIMPLE" },
  },
  data() {
    return {
      tabActivo: 0,
      acciones: {},
      seleccionManual: {},
      editOverrides: {},
      descuentoGlobal: null,
      productosBusqueda: {},
      loadingBusqueda: {},
      searchTimeouts: {},
      modalCalculoPrecios: false,
      filaCalculoIndex: null,
    };
  },
  computed: {
    itemsIndexados() {
      return (this.items || []).map((item, i) => ({ ...item, _tabIndex: i }));
    },
    itemsConMatch() {
      return this.itemsIndexados.filter((item) => item.metodoMatch !== "sin_match");
    },
    itemsSinMatch() {
      return this.itemsIndexados.filter((item) => item.metodoMatch === "sin_match");
    },
    itemsEnTab() {
      if (this.tabActivo === 1) return this.itemsConMatch;
      if (this.tabActivo === 2) return this.itemsSinMatch;
      return this.itemsIndexados;
    },
    etiquetaFilaCalculoModal() {
      if (this.filaCalculoIndex == null) return "";
      return this.getNombreCalculoFila(this.filaCalculoIndex);
    },
    cantidadAsociar() {
      return this.itemsIndexados.filter((item) => {
        const prod = this.getProductoParaFila(item, item._tabIndex);
        return prod && this.acciones[item._tabIndex] === "asociar";
      }).length;
    },
    modalCalculoData() {
      const idx = this.filaCalculoIndex;
      if (idx == null) return null;
      const item = this.itemsIndexados[idx];
      if (!item) return null;
      const producto = this.getProductoParaFila(item, idx);
      if (!producto) return { sinProducto: true };
      const descActual = item.descuentoActual != null ? Number(item.descuentoActual) : 0;
      const descNuevo = this.getDescuentoEfectivo(item, idx);
      const precioCompraNuevo = this.getPrecioCompraEfectivo(item, idx);
      const actual = this.armarDesglose(producto, item.precioCompraActual, descActual);
      const nuevo  = this.armarDesglose(producto, precioCompraNuevo, descNuevo);
      let precioVentaGuardado = null;
      const pvRaw = producto.precio != null ? producto.precio : producto.precioVenta;
      if (pvRaw != null && pvRaw !== "") {
        const pv = Number(pvRaw);
        if (!Number.isNaN(pv)) precioVentaGuardado = pv;
      }
      const notaPrecioGuardadoActual = actual.ok && precioVentaGuardado != null &&
        Math.abs(precioVentaGuardado - actual.precioFinal) > 0.015;
      const nuevoMayorQueActual = actual.ok && nuevo.ok &&
        Number(nuevo.precioFinal) > Number(actual.precioFinal) + 0.005;
      return {
        sinProducto: false, actual, nuevo, precioVentaGuardado,
        notaPrecioGuardadoActual, nuevoMayorQueActual,
        ivaProducto: Number(producto.ivaPorcentaje) || 0,
        margenProducto: Number(producto.porcentajeSobreCosto) || 0,
        tipoRedondeoProducto: producto.tipoRedondeo || "ninguno",
      };
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          const next = {};
          val.forEach((item, i) => { next[i] = item.producto ? "asociar" : "ignorar"; });
          this.acciones = next;
          this.seleccionManual = {};
          this.editOverrides = {};
          this.$nextTick(() => this.emitCantidadParaAplicar());
        }
      },
    },
    modalCalculoPrecios(val) {
      if (!val) this.filaCalculoIndex = null;
    },
  },
  methods: {
    // ── Edit overrides ──
    ensureOverride(index) {
      if (!Object.prototype.hasOwnProperty.call(this.editOverrides, index)) {
        this.$set(this.editOverrides, index, { precioCompra: null, descuento: null, precioVentaManual: null });
      }
      return this.editOverrides[index];
    },
    getPrecioCompraEfectivo(item, index) {
      const ov = this.editOverrides[index];
      if (ov && ov.precioCompra !== null && ov.precioCompra !== undefined && ov.precioCompra !== "")
        return Number(ov.precioCompra);
      return item.precioCompraNuevo;
    },
    getDescuentoEfectivo(item, index) {
      const ov = this.editOverrides[index];
      if (ov && ov.descuento !== null && ov.descuento !== undefined && ov.descuento !== "")
        return Number(ov.descuento);
      return item.descuentoNuevo != null ? Number(item.descuentoNuevo) : 0;
    },
    getPrecioVentaManualOverride(index) {
      const ov = this.editOverrides[index];
      if (ov && ov.precioVentaManual !== null && ov.precioVentaManual !== undefined && ov.precioVentaManual !== "")
        return ov.precioVentaManual;
      return null;
    },
    getPrecioVentaNuevoCalculado(item, index) {
      const p = this.getProductoParaFila(item, index);
      if (!p) return null;
      const ov = this.editOverrides[index];
      if (ov && ov.precioVentaManual !== null && ov.precioVentaManual !== undefined && ov.precioVentaManual !== "")
        return Number(ov.precioVentaManual);
      return this.calcularPrecioVentaDesdeCompra(
        this.getPrecioCompraEfectivo(item, index),
        this.getDescuentoEfectivo(item, index),
        p
      );
    },
    // ── Input handlers ──
    onPrecioCompraInput(index, val) {
      const ov = this.ensureOverride(index);
      ov.precioCompra = val !== "" && val !== null ? Number(val) : null;
      ov.precioVentaManual = null;
    },
    onDescuentoInput(index, val) {
      const ov = this.ensureOverride(index);
      ov.descuento = val !== "" && val !== null ? Number(val) : null;
      ov.precioVentaManual = null;
    },
    onPrecioVentaManualInput(index, val) {
      const ov = this.ensureOverride(index);
      ov.precioVentaManual = val !== "" && val !== null ? Number(val) : null;
    },
    // ── Global controls ──
    aplicarDescuentoGlobal() {
      const val = this.descuentoGlobal;
      if (val === null || val === undefined || val === "") return;
      this.itemsIndexados.forEach((item) => {
        const ov = this.ensureOverride(item._tabIndex);
        ov.descuento = Number(val);
        ov.precioVentaManual = null;
      });
    },
    limpiarDescuentoGlobal() {
      this.descuentoGlobal = null;
      this.itemsIndexados.forEach((item) => {
        const ov = this.editOverrides[item._tabIndex];
        if (ov) { ov.descuento = null; ov.precioVentaManual = null; }
      });
    },
    marcarTodosAsociar() {
      this.itemsIndexados.forEach((item) => {
        const prod = this.getProductoParaFila(item, item._tabIndex);
        if (prod) this.$set(this.acciones, item._tabIndex, "asociar");
      });
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    marcarTodosIgnorar() {
      this.itemsIndexados.forEach((item) => { this.$set(this.acciones, item._tabIndex, "ignorar"); });
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    // ── Diferencia ──
    getDiferenciaEfectiva(item, index) {
      const precioNuevo  = this.getPrecioCompraEfectivo(item, index);
      const precioActual = item.precioCompraActual;
      if (precioActual == null || precioActual <= 0) return null;
      return parseFloat((((precioNuevo - precioActual) / precioActual) * 100).toFixed(2));
    },
    getDiferenciaColorEfectiva(item, index) {
      const d = this.getDiferenciaEfectiva(item, index);
      if (d == null) return "grey";
      return d < 0 ? "success" : d > 0 ? "error" : "grey";
    },
    diffBadgeClass(item, index) {
      const d = this.getDiferenciaEfectiva(item, index);
      if (d == null) return "diff-neutral";
      if (d < 0) return "diff-down";
      if (d > 0) return "diff-up";
      return "diff-neutral";
    },
    // ── Match ──
    getMatchTagStyle(metodoMatch) {
      const styles = {
        codigo_barras:    { background: "#E0F2F1", color: "#00695C", borderColor: "#80CBC4" },
        nombre_aprendido: { background: "#E3F2FD", color: "#1565C0", borderColor: "#90CAF9" },
        nombre:           { background: "#FFF3E0", color: "#E65100", borderColor: "#FFCC80" },
        sin_match:        { background: "#F5F5F5", color: "#757575", borderColor: "#E0E0E0" },
      };
      return styles[metodoMatch] || styles.sin_match;
    },
    getMatchLabel(metodoMatch) {
      const map = {
        codigo_barras:    this.$t("actualizarPreciosProveedor.badgeCodigo"),
        nombre_aprendido: this.$t("actualizarPreciosProveedor.badgeAprendido"),
        nombre:           this.$t("actualizarPreciosProveedor.badgeNombre"),
        sin_match:        this.$t("actualizarPreciosProveedor.badgeSinMatch"),
      };
      return map[metodoMatch] || metodoMatch;
    },
    // ── Row class ──
    filaClass(item, index) {
      const prod = this.getProductoParaFila(item, index);
      if (!prod) return { "row-pending": true };
      if (this.acciones[index] === "asociar") return { "row-asociar": true };
      if (this.acciones[index] === "ignorar") return { "row-ignorar": true };
      return {};
    },
    // ── Modal ──
    abrirModalCalculoFila(index) { this.filaCalculoIndex = index; this.modalCalculoPrecios = true; },
    cerrarModalCalculoFila() { this.modalCalculoPrecios = false; },
    getNombreCalculoFila(index) {
      const item = this.itemsIndexados[index];
      if (!item) return "—";
      const p = this.getProductoParaFila(item, index);
      if (p) return this.getProductoNombre(p);
      return item.nombreArchivo || item.codigoBarrasProveedor || "—";
    },
    // ── Producto helpers ──
    getProductoParaFila(item, index) {
      if (Object.prototype.hasOwnProperty.call(this.seleccionManual, index))
        return this.seleccionManual[index] || null;
      return (item && item.producto) || null;
    },
    getItemsAutocomplete(index, item) {
      const search = this.productosBusqueda[index] || [];
      const sel = this.getProductoParaFila(item, index);
      if (!sel) return search;
      const sid = String(sel.id || sel._id || "");
      const exists = search.some((p) => String(p.id || p._id || "") === sid);
      if (exists) return search;
      return [sel, ...search];
    },
    getProductoNombre(p) {
      if (!p) return "—";
      const tiene = p.marca && p.marca.nombre && p.modelo && p.modelo.nombre && p.tamanio && p.tamanio.tamanio;
      if (this.modo === "ESTRUCTURADO" || tiene) {
        const parts = [p.marca && p.marca.nombre, p.modelo && p.modelo.nombre, p.tamanio && p.tamanio.tamanio].filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : p.nombre || p.codigoBarras || p.id || "—";
      }
      return p.nombre || p.codigoBarras || p.id || "—";
    },
    getPrecioVentaActualMostrado(item, index) {
      const p = this.getProductoParaFila(item, index);
      const v = p && (p.precio != null ? p.precio : p.precioVenta);
      if (v == null || v === "") return null;
      return Number(v);
    },
    // ── Cálculo precio ──
    armarDesglose(producto, precioCompra, descuentoPct) {
      const pc = precioCompra != null ? Number(precioCompra) : NaN;
      const d  = Number(descuentoPct) || 0;
      if (!producto || Number.isNaN(pc) || pc <= 0) return { ok: false };
      const iva    = Number(producto.ivaPorcentaje) || 0;
      const margen = Number(producto.porcentajeSobreCosto) || 0;
      const tr     = producto.tipoRedondeo || "ninguno";
      const neto   = pc * (1 - d / 100);
      const trasIva = neto * (1 + iva / 100);
      const antesRedondeo = trasIva * (1 + margen / 100);
      let final = antesRedondeo;
      if (final > 0 && tr !== "ninguno") {
        switch (tr) {
          case "centavos":     final = Math.ceil(antesRedondeo); break;
          case "multiplo_10":  final = Math.ceil(antesRedondeo / 10) * 10; break;
          case "multiplo_100": final = Math.ceil(antesRedondeo / 100) * 100; break;
        }
      }
      final = parseFloat(final.toFixed(2));
      return { ok: true, precioCompra: pc, descuentoPct: d, neto, ivaPct: iva, trasIva, margenPct: margen, antesRedondeo, tipoRedondeo: tr, precioFinal: final, redondeoAplica: tr !== "ninguno" && antesRedondeo > 0 };
    },
    calcularPrecioVentaDesdeCompra(precioCompra, descuentoProv, producto) {
      const res = this.armarDesglose(producto, precioCompra, descuentoProv);
      return res.ok ? res.precioFinal : null;
    },
    moneda(n) {
      if (n == null || Number.isNaN(Number(n))) return "—";
      return `$${Number(n).toFixed(2)}`;
    },
    etiquetaRedondeo(tr) {
      const map = { ninguno: "redondeoNinguno", centavos: "redondeoCentavos", multiplo_10: "redondeoMultiplo10", multiplo_100: "redondeoMultiplo100" };
      return this.$t(`actualizarPreciosProveedor.${map[tr] || "redondeoNinguno"}`);
    },
    formatNum(n)       { return Number(n).toFixed(2); },
    formatDiferencia(pct) { const n = Number(pct); return `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`; },
    // ── Acciones ──
    setAccion(index, accion) {
      this.$set(this.acciones, index, accion);
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    onSeleccionManual(index, selected) {
      if (selected && typeof selected === "object") {
        this.$set(this.seleccionManual, index, { ...selected, id: selected.id || selected._id });
        this.$set(this.acciones, index, "asociar");
      } else {
        this.$set(this.seleccionManual, index, null);
        this.$set(this.acciones, index, "ignorar");
      }
      this.$nextTick(() => this.emitCantidadParaAplicar());
    },
    onSearchProducto(index, e) {
      const q = typeof e === "string" ? e.trim() : (e && e.target && e.target.value) ? String(e.target.value).trim() : "";
      if (this.searchTimeouts[index]) clearTimeout(this.searchTimeouts[index]);
      if (!q || q.length < 2) { this.$set(this.productosBusqueda, index, []); return; }
      this.searchTimeouts[index] = setTimeout(() => {
        this.$set(this.loadingBusqueda, index, true);
        ProductoProvider.searchProductosByMarcaModelo(q, this.depositoId)
          .then((res) => {
            const items = (res && res.data && res.data.productoSearchByMarcaModelo) || [];
            this.$set(this.productosBusqueda, index, items.map((p) => ({ ...p, id: p.id || p._id })));
          })
          .catch(() => this.$set(this.productosBusqueda, index, []))
          .finally(() => this.$set(this.loadingBusqueda, index, false));
      }, 300);
    },
    emitCantidadParaAplicar() {
      this.$emit("update:cantidadParaAplicar", this.getItemsParaAplicar().length);
    },
    getItemsParaAplicar() {
      return this.itemsIndexados
        .filter((item) => {
          const producto = this.getProductoParaFila(item, item._tabIndex);
          return producto && this.acciones[item._tabIndex] === "asociar";
        })
        .map((item) => {
          const producto = this.getProductoParaFila(item, item._tabIndex);
          return {
            productoId:            producto.id || producto._id,
            codigoBarrasProveedor: item.codigoBarrasProveedor || "",
            nombreArchivo:         item.nombreArchivo || "",
            precioCompra:          this.getPrecioCompraEfectivo(item, item._tabIndex),
            descuentoProveedor:    this.getDescuentoEfectivo(item, item._tabIndex),
            precioVentaManual:     this.getPrecioVentaNuevoCalculado(item, item._tabIndex),
          };
        });
    },
  },
};
</script>

<style scoped>
/* ══════════════════════
   ROOT
══════════════════════ */
.revision-root { font-size: 13px; }
.revision-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  color: #BDBDBD;
}
.revision-empty-text { margin-top: 12px; font-size: 14px; }
.text-muted { color: #9E9E9E; }

/* ══════════════════════
   GLOBAL CONTROLS
══════════════════════ */
.gc-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 20px;
  background: #F8F9FA;
  border-bottom: 1px solid #E8EAED;
}
.gc-bar-left { display: flex; align-items: center; }
.gc-bar-right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.gc-label { font-size: 12px; font-weight: 600; color: #5F6368; }
.gc-descuento {
  position: relative;
  display: flex;
  align-items: center;
}
.gc-input {
  border: 1.5px solid #E0E0E0;
  border-radius: 6px;
  padding: 5px 28px 5px 10px;
  font-size: 13px;
  color: #202124;
  background: #fff;
  outline: none;
  width: 160px;
  transition: border-color 0.15s;
}
.gc-input:focus { border-color: #1976D2; }
.gc-input-suffix {
  position: absolute;
  right: 10px;
  font-size: 12px;
  color: #9E9E9E;
  pointer-events: none;
}
.gc-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 500;
  border: 1.5px solid #E0E0E0;
  background: #fff;
  color: #5F6368;
  cursor: pointer;
  transition: all 0.15s;
}
.gc-btn:hover { background: #F1F3F4; border-color: #BDBDBD; }
.gc-btn--primary {
  background: #1976D2;
  border-color: #1976D2;
  color: #fff;
}
.gc-btn--primary:hover { background: #1565C0; border-color: #1565C0; }

/* ══════════════════════
   TABS
══════════════════════ */
.tab-row {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0 20px;
  border-bottom: 2px solid #E8EAED;
  background: #FAFAFA;
}
.tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #5F6368;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.tab-pill:hover { color: #1976D2; }
.tab-pill--active { color: #1976D2; border-bottom-color: #1976D2; }
.tab-pill--warn { color: #E65100; }
.tab-pill--warn.tab-pill--active { border-bottom-color: #E65100; }
.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #F1F3F4;
  color: #5F6368;
  border-radius: 10px;
  min-width: 22px;
  height: 18px;
  font-size: 11px;
  font-weight: 700;
  padding: 0 5px;
}
.tab-pill--active .tab-count { background: #E3F2FD; color: #1565C0; }

/* ══════════════════════
   LEGEND
══════════════════════ */
.legend-row {
  display: flex;
  gap: 16px;
  padding: 8px 20px;
  background: #FAFAFA;
  border-bottom: 1px solid #F0F0F0;
}
.legend-item { display: flex; align-items: center; gap: 5px; font-size: 11.5px; color: #757575; }
.legend-dot {
  width: 10px; height: 10px; border-radius: 2px;
  border: 1px solid rgba(0,0,0,.1);
}
.legend-dot--asociar  { background: #C8E6C9; }
.legend-dot--ignorar  { background: #FFE0B2; }
.legend-dot--pending  { background: #FFF9C4; }

/* ══════════════════════
   TABLE WRAPPER
══════════════════════ */
.rev-table-wrapper {
  position: relative;
  overflow: hidden;
}
.rev-table-scroll {
  overflow-x: auto;
  max-height: 520px;
  overflow-y: auto;
}
.rev-fade-right {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: 24px;
  background: linear-gradient(to right, transparent, rgba(255,255,255,.85));
  pointer-events: none;
}

/* ══════════════════════
   TABLE
══════════════════════ */
.rev-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 1200px;
}

/* Header cells */
.rth {
  padding: 9px 10px;
  background: #F8F9FA;
  border-bottom: 2px solid #E8EAED;
  border-right: 1px solid #F0F0F0;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5F6368;
  white-space: nowrap;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 1;
}
.rth:last-child { border-right: none; }
.rth-fixed  { width: 36px; min-width: 36px; text-align: center; }
.rth-match  { min-width: 90px; }
.rth-producto { min-width: 200px; }
.rth-input    { min-width: 120px; }
.rth-input-sm { min-width: 80px; }
.rth-dif      { min-width: 70px; text-align: center; }
.rth-accion   { min-width: 150px; text-align: center; }

/* Data rows */
.rev-row { transition: filter 0.1s; }
.rev-row:hover { filter: brightness(0.97); }

.row-asociar td  { background: #F1F8E9; }
.row-ignorar td  { background: #FFF8F0; }
.row-pending td  { background: #FFFDE7; }

/* Data cells */
.rtd {
  padding: 6px 10px;
  border-bottom: 1px solid #F5F5F5;
  border-right: 1px solid #F0F0F0;
  vertical-align: middle;
  color: #3C4043;
}
.rtd:last-child { border-right: none; }

.rtd-star, .rtd-calc { text-align: center; width: 36px; }
.rtd-editable { padding: 3px 6px; }

/* Archivo column */
.rtd-archivo { min-width: 160px; max-width: 200px; }
.archivo-nombre { font-size: 12.5px; font-weight: 500; color: #202124; line-height: 1.3; }
.archivo-codigo { font-size: 11px; color: #80868B; margin-top: 2px; font-family: monospace; }

/* Match tag */
.match-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 600;
  border-width: 1px;
  border-style: solid;
  white-space: nowrap;
}

/* Precio actual */
.precio-tachado { font-size: 11px; text-decoration: line-through; color: #9E9E9E; }
.precio-neto    { font-size: 13px; font-weight: 500; color: #202124; }
.descuento-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 3px;
  background: #ECEFF1;
  color: #455A64;
}

/* Diff badge */
.diff-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.diff-down    { background: #E8F5E9; color: #1B5E20; }
.diff-up      { background: #FFEBEE; color: #B71C1C; }
.diff-neutral { background: #F5F5F5; color: #616161; }

/* Calc button */
.calc-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 4px;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.calc-btn:hover { background: #F1F3F4; }

/* Producto autocomplete */
::v-deep .rev-producto-select.table-input-producto { max-width: 100%; min-width: 180px; }
::v-deep .rev-producto-select .v-input__control { min-height: 32px !important; }
::v-deep .rev-producto-select .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #C5CAE9 !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  margin-bottom: 0 !important;
  transition: border-color 0.15s;
  box-shadow: none !important;
}
::v-deep .rev-producto-select .v-input__slot:hover  { border-color: #7986CB !important; }
::v-deep .rev-producto-select.v-input--is-focused .v-input__slot { border-color: #3F51B5 !important; box-shadow: 0 0 0 3px rgba(63,81,181,.15) !important; }
::v-deep .rev-producto-select .v-select__selection, ::v-deep .rev-producto-select input { color: #1A237E; font-weight: 600; font-size: 12.5px; }

/* Editable inputs */
::v-deep .table-input         { max-width: 110px; }
::v-deep .table-input-precio-nuevo { max-width: 130px; }
::v-deep .table-input-small   { max-width: 76px; }
::v-deep .table-input .v-input__control,
::v-deep .table-input-small .v-input__control { min-height: 32px !important; }
::v-deep .table-input .v-input__slot,
::v-deep .table-input-small .v-input__slot {
  background: #fff !important;
  border: 1.5px solid #C5CAE9 !important;
  border-radius: 6px !important;
  padding: 0 8px !important;
  margin-bottom: 0 !important;
  box-shadow: none !important;
  transition: border-color 0.15s, box-shadow 0.15s;
}
::v-deep .table-input .v-input__slot:hover,
::v-deep .table-input-small .v-input__slot:hover { border-color: #7986CB !important; }
::v-deep .table-input.v-input--is-focused .v-input__slot,
::v-deep .table-input-small.v-input--is-focused .v-input__slot {
  background: #E8EAF6 !important;
  border-color: #3F51B5 !important;
  box-shadow: 0 0 0 3px rgba(63,81,181,.18) !important;
}
::v-deep .table-input input[type="number"],
::v-deep .table-input-small input[type="number"] {
  text-align: center; font-weight: 600; font-size: 13px;
  color: #1A237E; -moz-appearance: textfield;
}
::v-deep .table-input input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-inner-spin-button,
::v-deep .table-input-small input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; }
::v-deep .table-input .v-text-field__prefix,
::v-deep .table-input .v-text-field__suffix,
::v-deep .table-input-small .v-text-field__suffix { color: #1A237E; font-weight: 600; font-size: 13px; }
::v-deep .table-input input::placeholder { color: #1A237E; font-weight: 600; opacity: .7; }

/* Action buttons */
.row-actions { display: flex; gap: 4px; align-items: center; justify-content: center; }
.action-btn {
  padding: 3px 9px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 600;
  border: 1.5px solid;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.action-btn--ignorar     { border-color: #E0E0E0; background: #fff;     color: #757575; }
.action-btn--ignorar:hover { background: #FFF3E0; border-color: #FFCC02; color: #E65100; }
.action-btn--ignorar-on  { border-color: #FF8F00; background: #FFF3E0; color: #E65100; }
.action-btn--asociar     { border-color: #E0E0E0; background: #fff;     color: #757575; }
.action-btn--asociar:hover { background: #E8F5E9; border-color: #4CAF50; color: #2E7D32; }
.action-btn--asociar-on  { border-color: #4CAF50; background: #E8F5E9; color: #1B5E20; }

/* Summary footer */
.revision-summary {
  display: flex;
  align-items: center;
  padding: 10px 20px;
  background: #F1F8E9;
  border-top: 1px solid #C8E6C9;
  font-size: 13px;
  color: #2E7D32;
}

/* Modal desglose */
.desglose-pasos { font-size: 13px; }
.dp { display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; align-items: baseline; }
.dp-formula, .dp-detail { grid-column: 1 / -1; }
.dp-label { color: rgba(0,0,0,.72); line-height: 1.35; }
.dp-val   { font-variant-numeric: tabular-nums; font-weight: 500; text-align: right; white-space: nowrap; }
.dp--hl .dp-val { font-weight: 700; }
.dp-final {
  background: rgba(0,0,0,.04);
  border: 1px solid rgba(0,0,0,.08);
}
.dp-final-val { font-size: 1.35rem; font-weight: 700; }
</style>
